import React from 'react';
import { message } from 'antd';
import { Badge } from '../ui/badge';
import {
  FileText,
  Truck,
  Star,
  ChevronRight
} from 'lucide-react';
import { ContainerTask, StatusConfig, TaskTypeConfig } from '../../types/container';
import { RouteDisplay } from './RouteDisplay';
import { FixPlateButton } from './FixPlateButton';
import { GateFeeButton } from './GateFeeButton';
import { BatchBookButton } from './BatchBookButton';
import { ClosedAreaButton } from './ClosedAreaButton';
import { GensetMark } from '../shared/GensetMark';
import { HoldReasons } from '../shared/HoldReasons';
import { formatLfdMedium, lfdDateOnly } from '../../utils/lfdDate';
import { containerKindLabel } from '../../utils/containerTypeLabel';
import scraperApi from '../../services/scraperApi';
import { gateOutMarker, completedMarker, GATE_OUT_STATUSES, hasLivePortAppt, orderCreatedMarker, ORDER_CREATED_STATUSES } from '../../utils/apptSchedule';

interface TaskTableRowProps {
  task: ContainerTask
  isEvenRow: boolean
  isMarked: boolean
  toggleMarked: (taskId: string) => void
  statusConfig: Record<string, StatusConfig>
  taskTypeConfig: Record<string, TaskTypeConfig>
  formatDateTime: (dateString: string) => string
  getLFDUrgency: (lfdDate: string) => string
  getLFDStyles: (urgency: string) => string
  /** 该表格不做行拖拽（拖动只会拖出一张半透明行影，没有落点），保留为可选以兼容旧调用方 */
  onDragStart?: (e: React.DragEvent, taskId: string) => void
  onDragEnd?: (e: React.DragEvent) => void
  onUpdateContainer: (updatedContainer: ContainerTask) => Promise<void>
  onOpenDetail: (task: ContainerTask) => void
  /** B3 Fix Plate — opens eModal for the appointment carrying the stale plate. */
  onFixPlate?: (appointmentId: number) => void
  /** Gate Fee — opens eModal's fee payment page for this task's container. */
  onGateFee?: (taskId: number | string) => void
  /** Batch Book —— 打开批量预约弹窗，从这只柜出发列出能一起约的柜。 */
  onBatchBook?: (taskId: number | string) => void
  selectedOrderingParty?: string
  onOrderingPartyClick?: (name: string) => void
}

function getStatusBorderColor(status: string): string {
  switch (status) {
    case 'picking':
    case 'delivering':
    case 'loading':
      return 'border-l-blue-500'
    case 'released':
    case 'assigned':
      return 'border-l-green-500'
    case 'on-hold':
    case 'paused':
    case 'pcc-hold':
      return 'border-l-orange-500'
    case 'completed':
      return 'border-l-emerald-500'
    case 'cancelled':
      return 'border-l-rose-500'
    case 'on-vessel':
      return 'border-l-purple-500'
    case 'unassigned':
      return 'border-l-amber-500'
    default:
      return 'border-l-slate-200'
  }
}

const activeStatuses = new Set(['picking', 'delivering', 'loading'])

export const TaskTableRow: React.FC<TaskTableRowProps> = ({
  task,
  isEvenRow,
  isMarked,
  toggleMarked,
  statusConfig,
  taskTypeConfig,
  formatDateTime,
  getLFDUrgency,
  getLFDStyles,
  onUpdateContainer,
  onOpenDetail,
  onFixPlate,
  onGateFee,
  onBatchBook,
  selectedOrderingParty,
  onOrderingPartyClick,
}) => {
  const lfdUrgency = getLFDUrgency(task.lfd)
  const isActive = activeStatuses.has(task.status)
  // 干货还是冷冻。判不出来是 null —— 那一格就不画，不给它兜个 Dry。
  const kindLabel = containerKindLabel(task.containerSize, task.containerType)
  // 「出来几天了」——和派单板上同一个标签，但这里有预约也照显：这一列不显示预约
  // 时间，没什么可抢的，而滞期费不会因为约上了就停下来。
  const gateOut = React.useMemo(
    () => (GATE_OUT_STATUSES.has(task.status) ? gateOutMarker(task, new Date(), { evenWhenBooked: true }) : null),
    [task],
  )
  // 单是哪天建的。只有还在船上的行有 —— 码头那边一个日期都还没给，这是整行
  // 唯一能说明「这单等了多久」的时间。
  const orderCreated = React.useMemo(
    () => (ORDER_CREATED_STATUSES.has(task.status) ? orderCreatedMarker(task) : null),
    [task],
  )
  // 干完那一程是哪天。只有已完成的行有 —— 其余状态还没这个日子。
  const completed = React.useMemo(
    () => (task.status === 'completed' ? completedMarker(task) : null),
    [task],
  )
  // 查验站的免费期最后一天，写成一行字。
  // 认不出形状的原样显示 PCC 自己的写法 —— 这一格是拿给人看的，宁可显示一个
  // 怪样子的日期，也不要把它变成一个「-」让人以为 PCC 什么都没说。
  const pccLfdLabel = React.useMemo(() => {
    const raw = task.pccLastFreeDay
    if (!raw) return '--'
    return lfdDateOnly(raw) ? formatLfdMedium(raw) : raw
  }, [task.pccLastFreeDay])
  // 点一下那个日子 → 开 PCC 上这个柜号的结果页。
  //
  // 地址得现问后端：钥匙存在服务端不下发，而 PCC 的结果页是按「这一票的流水号」
  // 寻址的，那个号只有提交一次查询才换得到。所以这一下有等待。
  const [pccOpening, setPccOpening] = React.useState(false)
  const openPccPage = React.useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (pccOpening) return
    // 先同步开一个空标签页，再去问地址。反过来写（拿到地址才 window.open）会被
    // 浏览器判成「不是用户点出来的」弹窗，直接拦掉 —— 表现就是点了没反应。
    const tab = window.open('', '_blank')
    if (!tab) {
      message.error('Allow pop-ups for this site to open the PCC page')
      return
    }
    setPccOpening(true)
    try {
      const { url } = await scraperApi.getPccResultPageUrl(task.id)
      tab.location.href = url
    } catch (err) {
      // 开都开了，没拿到地址就得把那个空标签页收掉，不然留一张永远空白的页。
      tab.close()
      const failure = err as { code?: string; response?: { data?: { message?: string } } }
      message.error(
        failure.code === 'ECONNABORTED'
          ? 'PCC is taking too long to answer — try again in a moment'
          : failure.response?.data?.message || 'Could not open the PCC page',
      )
    } finally {
      setPccOpening(false)
    }
  }, [task.id, pccOpening])
  // 「On Hold」四个字只说了柜子动不了，没说是海关、船司还是欠一笔码头费 ——
  // 而这三种的处置完全不同。原因只有柜子监控说得出来（任务表上没有这一列），
  // 所以照它的原话挂在状态底下，和柜子监控弹窗 Holds 那一列是同一套措辞和颜色。
  const holdReasonCount = (task.holdDetails?.length ?? 0) + (task.holdReasons?.length ?? 0)
  // 原因是一句转述，转述就得带上时间：监控一停，那几个标签就是一张再也不会被
  // 推翻的快照，不写明「什么时候问到的」就等于拿旧话冒充现在的事实。
  const holdFootnote = React.useMemo(() => {
    const parts: string[] = []
    if (task.holdCheckedAt) parts.push(`Checked ${formatDateTime(task.holdCheckedAt)}`)
    if (task.holdMonitorActive === false) parts.push('Monitoring stopped — last known reason')
    return parts.length ? parts.join(' · ') : undefined
  }, [task.holdCheckedAt, task.holdMonitorActive, formatDateTime])
  // 下单方 = 发邮件的下单方，绝不回退到 shipper（两者是不同角色，混显会误导调度）
  const orderingPartyName = task.orderingPartyName
  const isOrderingPartyFiltered = !!orderingPartyName && selectedOrderingParty === orderingPartyName

  return (
      <tr
        className={`border-b border-slate-100 border-l-[3px] ${getStatusBorderColor(task.status)} hover:bg-blue-50/60 hover:translate-x-[2px] hover:shadow-sm transition-all duration-150 ${
          isEvenRow ? "bg-slate-50/50" : "bg-white"
        } ${isMarked ? "ring-1 ring-orange-300 bg-orange-50/50" : ""}`}
      >
        {/* Task ID */}
        <td className="px-3 py-3 w-[140px] min-w-[140px]">
          <div className="flex items-center gap-1">
            <button
              onClick={() => onOpenDetail(task)}
              className="p-0.5 hover:bg-slate-200 rounded transition-colors"
            >
              <ChevronRight className="w-3 h-3" />
            </button>
            <span
              onClick={() => onOpenDetail(task)}
              className="text-blue-600 font-medium text-sm hover:text-blue-700 cursor-pointer"
            >
              {task.taskId}
            </span>
            {/* B3 Fix Plate — the same button as the one on the Port node card
                in the task detail modal. Red and pulsing IS the alert; there is
                no separate marker to keep in sync. Server-derived, so it
                survives refresh and disappears on its own once the plate is
                verified. Clicking opens eModal directly — no need to go through
                the detail modal first. */}
            {task.plateFixAppointmentId != null && onFixPlate && (
              <FixPlateButton
                state="needs_fix"
                stopPropagation
                onClick={() => onFixPlate(task.plateFixAppointmentId as number)}
              />
            )}
            {/* Gate Fee —— 只在「只差这一笔码头费就能放行」的柜子上出现。
                以前是每张进口任务都挂一个，因为当时没有可靠的「这只柜欠费」信号；
                现在有了（三家码头各自的判据 → awaitingGateFee），按钮就该跟着
                那个信号走，而不是到处都摆一个让人一个个去点。 */}
            {task.awaitingGateFee && onGateFee && (
              <GateFeeButton
                state={task.gateFeePaid ? 'paid' : 'idle'}
                stopPropagation
                onClick={() => onGateFee(task.id)}
              />
            )}
            {/* 批量预约 —— 只挂在 released 的行上：放行了、还没约的柜就是该去约的
                那一批。点开的弹窗会把同码头同动作的其它 released 柜一起列出来，
                勾几只就一次约几只（eModal 一个向导最多装 10 只）。

                「已经有预约的柜不会是 released」这个假设是错的：约了没去、票过了期，
                柜子仍然停在 released。所以这里必须自己再判一次有没有活票，判据和
                后端同一条（hasLivePortAppt）—— 不然按钮亮着、点下去却被后端顶回来。 */}
            {task.status === 'released' && !hasLivePortAppt(task) && onBatchBook && (
              <BatchBookButton stopPropagation onClick={() => onBatchBook(task.id)} />
            )}
            {/* CA —— 已经派出去又被封闭区拉回来的，和 Gate Fee 占同一个位置。
                两者互斥：封闭区的柜子拿不出来，付码头费也没用。 */}
            {task.closedArea && task.recalledFromDispatch && (
              <ClosedAreaButton stopPropagation onClick={() => onOpenDetail?.(task)} />
            )}
          </div>
        </td>

        {/* Container Info */}
        <td className="px-3 py-3 w-[180px] min-w-[180px]">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-slate-900 text-sm leading-tight">
                {task.containerNumber}
              </span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                task.importExport === 'export'
                  ? 'bg-orange-100 text-orange-600'
                  : 'bg-blue-100 text-blue-600'
              }`}>
                {task.importExport === 'export' ? 'EXP' : 'IMP'}
              </span>
            </div>
            {/* 订舱号 —— 出口单在提柜之前根本没有柜号，第一行只剩一个 EXP 徽标，
                整行认不出是哪一票。订舱号是那段时间里唯一能认人的号，所以紧跟在
                柜号下面。进口单一般没有这个号，有就照样显示，不另做区分。 */}
            {task.bookingNumber && (
              <div className="text-xs leading-tight flex items-center gap-1">
                <span className="text-[10px] font-semibold text-slate-400">BKG</span>
                <span className="font-mono text-slate-700">{task.bookingNumber}</span>
              </div>
            )}
            {/* 干货/冷冻判不出来就整格不画（连同前面那个分隔点）—— 以前是「不是
                reefer 就写 Dry」，于是没有值的柜子也被理直气壮标成干货，还画出过
                `20RF · Dry` 这种自己打自己脸的行。判法见 containerTypeLabel。 */}
            <div className="text-xs text-slate-600 leading-tight flex items-center gap-1">
              <span className="font-medium">{task.shippingLine}</span>
              <span className="text-slate-400">&middot;</span>
              <span>{task.containerSize}</span>
              {kindLabel && (
                <>
                  <span className="text-slate-400">&middot;</span>
                  <span className={kindLabel === "Reefer" ? "text-blue-600 font-medium" : ""}>
                    {kindLabel}
                  </span>
                </>
              )}
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-1">
              <Truck className="w-3 h-3" />
              <span className="font-mono">{task.chassisNumber}</span>
              {task.chassisCompany && !task.chassisId && (
                <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded">{task.chassisCompany}</span>
              )}
              {/* 要哪种发电机。跟在底盘后面，因为它和底盘一样是「这趟车上得带
                  什么」。两种画两种图标，详见 GensetMark。 */}
              <GensetMark gensetType={task.gensetType} />
            </div>
          </div>
        </td>

        {/* Status */}
        <td className="px-3 py-3 w-[130px] min-w-[130px]">
          <Badge
            className={`${statusConfig[task.status]?.bgColor || 'bg-gray-100'} ${
              statusConfig[task.status]?.textColor || 'text-gray-700'
            } border-0 rounded-full text-xs px-2 py-0.5 font-medium ${
              isActive ? 'animate-pulse' : ''
            }`}
          >
            {statusConfig[task.status]?.label || task.status}
          </Badge>
          {/* 「单是哪天建的」，跟在 On Vessel 下面。不上色不分档：柜子还在船上，
              这个日子只回答「排在谁前面」，没有哪一天是要人立刻动手的信号，
              染成红黄只会和真正要紧的那几条标签抢眼睛。 */}
          {orderCreated && (
            <div
              className="mt-1 w-fit whitespace-nowrap text-[10px] font-medium text-slate-500"
              title={orderCreated.title}
            >
              {orderCreated.label}
            </div>
          )}
          {/* On Hold 的理由 —— 紧跟在「On Hold」下面。
              这一列只有 130px，所以走短说法（`Not available` 而不是码头那句
              `NOT AVAILABLE FOR PICKUP`）：原话在这儿一律被切一半，一行柜子占三行
              高，切到一半的句子还不如一个短词说得清。完整原话没有丢，鼠标停上去
              就是（外加「什么时候问到的」）。
              一条都没有也照样占这一行：空着也是一句话（「谁都还没说」），位置
              忽有忽无反而让人以为是自己看漏了。 */}
          {task.status === 'on-hold' && (
            <div className="mt-1">
              {holdReasonCount > 0 ? (
                <HoldReasons
                  compact
                  details={task.holdDetails ?? null}
                  holds={task.holdReasons ?? null}
                  terminalHoldCodes={task.terminalHoldCodes}
                  footnote={holdFootnote}
                  className="max-w-[124px]"
                />
              ) : (
                <span
                  className="text-[10px] text-slate-400"
                  title="No terminal has said why this container is held — either container monitoring is not running for it, or the terminal has not published a reason"
                >
                  No reason reported
                </span>
              )}
            </div>
          )}
          {/* flex 而不是 inline-flex：inline-flex 是行内的，挤得下就跟在状态后面、
              挤不下才换行 —— 于是短一个字的「Out 9/9 · 1d」和状态并排，「Out 9/10 · 0d」
              却掉到下一行，同一列看上去参差不齐。w-fit 保证独占一行的同时底色只
              包住文字本身。 */}
          {gateOut && (
            <div
              className={`mt-1 flex w-fit items-center gap-1 rounded px-1 py-0.5 text-[10px] font-semibold whitespace-nowrap ring-1 ${
                gateOut.tone === 'stale'
                  ? 'bg-red-50 text-red-700 ring-red-200'
                  : gateOut.tone === 'aging'
                    ? 'bg-amber-50 text-amber-700 ring-amber-200'
                    : 'bg-slate-100 text-slate-600 ring-slate-200'
              }`}
              title={gateOut.title}
            >
              {gateOut.label}
            </div>
          )}
          {/* CBP Hold 那一行，状态下面挂一个查验站的免费期最后一天。
              这一列右边那个 LFD 是码头给的，柜子被海关拉走之后码头那条监控就
              停了，它从此不再动 —— 柜子待在查验站的这些天里还在计钱的是 PCC
              这个日子，所以它得跟着「CBP Hold」这四个字一起出现，而不是挤进
              那一列去和一个已经冻住的日期打架。
              查不到就显示 --：这一行永远在，空着也是一句话（「PCC 还没说」），
              位置忽有忽无反而让人以为是自己看漏了。 */}
          {task.status === 'pcc-hold' && (
            <div
              className={`mt-1 flex w-fit items-center gap-1 rounded px-1 py-0.5 text-[10px] font-semibold whitespace-nowrap ${
                task.pccLastFreeDay
                  ? getLFDStyles(getLFDUrgency(task.pccLastFreeDay))
                  : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200'
              }`}
            >
              <button
                type="button"
                onClick={openPccPage}
                disabled={pccOpening}
                className="underline-offset-2 hover:underline disabled:cursor-wait disabled:opacity-70"
                title={
                  task.pccLastFreeDay
                    ? `PCC Last Free Day: ${lfdDateOnly(task.pccLastFreeDay) ? formatLfdMedium(task.pccLastFreeDay, { year: true }) : task.pccLastFreeDay}\nClick to open this container's page on PCC`
                    : "PCC has not published a Last Free Day for this container yet\nClick to open this container's page on PCC"
                }
              >
                LFD {pccLfdLabel}
              </button>
              {/* 这个日子和右边那一列的 LFD 不是一回事，出处得写在脸上 ——
                  否则两个都叫 LFD、数还不一样，看的人只会当成哪一个坏了。 */}
              <span className="text-[8px] font-bold tracking-wider opacity-60">PCC</span>
            </div>
          )}
          {/* 完成日 —— 和出闸日同一个位置、同一身打扮，只是不分颜色档：活已经干完
              了，没有「越久越该动它」这回事，用不着催。 */}
          {completed && (
            <div
              className="mt-1 flex w-fit items-center gap-1 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold whitespace-nowrap text-slate-600 ring-1 ring-slate-200"
              title={completed.title}
            >
              {completed.label}
            </div>
          )}
        </td>

        {/* Ordering Party */}
        <td className="px-3 py-3 w-[120px] min-w-[120px] hidden md:table-cell">
          {orderingPartyName ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onOrderingPartyClick?.(orderingPartyName)
              }}
              title={
                isOrderingPartyFiltered
                  ? `Clear filter: ${orderingPartyName}`
                  : `Filter by ordering party: ${orderingPartyName}`
              }
              className={`block w-full text-left text-xs truncate rounded px-1 -mx-1 py-0.5 transition-colors ${
                isOrderingPartyFiltered
                  ? 'bg-[#00838F]/10 text-[#00838F] font-semibold'
                  : 'text-slate-700 hover:bg-slate-100 hover:text-[#00838F]'
              }`}
            >
              {orderingPartyName}
            </button>
          ) : (
            <span className="text-xs text-slate-400">--</span>
          )}
        </td>

        {/* LFD — a calendar day, so it gets its own formatter: the generic
            timestamp one both shifts the day in the viewer's timezone and
            invents a "05:00 PM" that no terminal ever published. */}
        <td className="px-3 py-3 w-[80px] min-w-[80px]">
          <span className={`text-xs font-medium px-2 py-0.5 rounded ${getLFDStyles(lfdUrgency)}`}>
            {task.lfd ? formatLfdMedium(task.lfd) : 'Pending'}
          </span>
        </td>

        {/* Task Type */}
        <td className="px-3 py-3 w-[110px] min-w-[110px] hidden lg:table-cell">
          <div className="flex flex-col items-center gap-1">
            <Badge
              className={`${taskTypeConfig[task.taskType]?.bgColor || 'bg-gray-100'} ${
                taskTypeConfig[task.taskType]?.textColor || 'text-gray-700'
              } border-0 rounded-full text-xs px-2 py-0.5 font-medium`}
            >
              {taskTypeConfig[task.taskType]?.label || task.taskType}
            </Badge>
            {task.taskSubtype && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                task.taskSubtype === 'live-load'
                  ? 'bg-purple-50 text-purple-600'
                  : 'bg-slate-100 text-slate-600'
              }`}>
                {task.taskSubtype === 'live-load' ? 'Live Load' : 'Drop'}
              </span>
            )}
          </div>
        </td>

        {/* Driver */}
        <td className="px-3 py-3 w-[100px] min-w-[100px]">
          <span className="text-xs text-slate-700 truncate" title={task.driver}>
            {task.driver}
          </span>
        </td>

        {/* Route */}
        <td className="px-3 py-3 w-[260px] min-w-[260px] hidden md:table-cell">
          <RouteDisplay task={task} />
        </td>

        {/* Actions */}
        <td className="px-3 py-3 w-[60px] min-w-[60px] text-right">
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onOpenDetail(task)
              }}
              className="p-1 hover:bg-blue-100 rounded transition-colors"
              title="View Task Details"
            >
              <FileText className="w-3 h-3 text-blue-500" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                toggleMarked(task.id)
              }}
              className="p-1 hover:bg-slate-200 rounded transition-colors"
              title={isMarked ? "Remove from favorites" : "Add to favorites"}
            >
              {isMarked ? (
                <Star className="w-3 h-3 text-orange-500 fill-current" />
              ) : (
                <Star className="w-3 h-3 text-slate-400" />
              )}
            </button>
          </div>
        </td>
      </tr>
  )
}
